import React, { useEffect, useState, Fragment } from 'react';
import { useParams } from 'react-router-dom';
import {
    Dialog,
    Box,
    Typography,
    IconButton,
    Stack,
    Divider,
    Avatar,
    CardMedia,
    ButtonBase,
    Button,
    CircularProgress
} from '@mui/material';
import { useTranslation } from "react-i18next";

import get from "lodash/get";
import map from "lodash/map";
import isEmpty from "lodash/isEmpty";
import { useDispatch, useSelector } from "react-redux";

import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'; 
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import { useCanView } from "../../../config/hook/UserPermissions";
import Alert from "../../../components/form/Alert";

import Notification from "../../../components/form/Notification";

import AttachFilesModalDP from "./AttachFilesModalDP";
import { deleteFiles } from "../../../store/transactions/thunk/filesRoute/delete";
import { postFiles } from "../../../store/transactions/thunk/filesRoute/post";
import { getRouteId } from "../../../store/transactions/thunk/route/getId";

const FilesModal = ({
    __,
    open,
    close,
    selected /** EL que fue seleccionado **/
}) => {
    const dispatch = useDispatch();
    const { id } = useParams();
    const [imgSelect, setImgSelect] = useState({});
    const [__g] = useTranslation("global");
    const [openModal, setOpenModal] = useState(false);
    const [showNoti, setShowNoti] = useState({ open: false, msg: "", variant: "error", action: "post" });

    const current = useSelector(state => state.transactions.route.detail);
    const delet = useSelector(state => state.transactions.file.delete);
    const post = useSelector(state => state.transactions.file.post);

    const getData = () => {
        dispatch(getRouteId( id ));
    };

    const [alert, setAlert] = useState({ open: false, title: "", subtitle: "", type: "" });
    const canView = useCanView();

    const reload = () => getData();

    useEffect(() => {
        if (get(current, "data.files.length") > 0) {
            setImgSelect(get(current, "data.files.[0]"));
        } else {
            setImgSelect({}); // Resetea a un objeto vacío si no hay archivos
        }
    }, [get(current, "isSuccess", false), open]);

    useEffect(() => {
        if (get(post, "isSuccess", false)) { reload(); setShowNoti({ open: true, msg: "", variant: "success", action: "post" }); }
        if (get(delet, "isSuccess", false)) { reload(); setShowNoti({ open: true, msg: "", variant: "success", action: "delete" }) }
    }, [post, delet]);
    
    const onClick = (item) => () => {
        setImgSelect(item);
    };

    const onClose = () => {
        setImgSelect({});
        close();
    };

    const closeAlert = () => {
        setAlert({ open: false, title: "", subtitle: "", type: "", btn: "" })
    }

    // Función para obtener el ícono según la extensión del archivo
    const getFileIcon = (fileName) => {
        const extension = fileName.split('.').pop().toLowerCase(); // Extraer la extensión
    
        switch (extension) {
            case 'pdf':
                return <PictureAsPdfIcon sx={{ fontSize: 40, color: 'red' }} />;
            case 'doc':
            case 'docx':
                return <DescriptionIcon sx={{ fontSize: 40, color: 'blue' }} />;
            case 'xls':
            case 'xlsx':
                return <TableChartIcon sx={{ fontSize: 40, color: 'green' }} />;
            case 'jpg':
            case 'jpeg':
            case 'png':
                return null; // No ícono, es imagen
            default:
                return <InsertDriveFileIcon sx={{ fontSize: 40, color: 'gray' }} />;
        }
    };

    const HandleUploadFile = (file) =>{
       dispatch(postFiles(file));
    }

    const onDelete = (documenteName) => {
       dispatch(deleteFiles(documenteName));
      };

    const renderPreview = (file) => {
        if (!file || !file.url || !file.fileName) { 
            return (
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%',
                    backgroundColor: 'lightgray'
                }}>
                    <Typography variant="caption">No hay vista previa disponible.</Typography>
                </Box>
            );
        }

        const fileUrl = file.url + file.fileName;
        const extension = file.fileName.split('.').pop().toLowerCase();

        if (['jpg', 'jpeg', 'png'].includes(extension)) {
            // Previsualizar imágenes
            return (
                <CardMedia
                    component="img"
                    image={file.url + file.fileName}
                    alt={`Vista previa de ${file.fileName}`}
                    sx={{ objectFit: "contain", height: "100%", width: "100%" }}
                />
            );
        } else if (extension === 'pdf') {
            // Previsualizar PDF usando iframe
            return (
                <iframe
                    src={file.url + file.fileName}
                    title={`Vista previa de ${file.fileName}`}
                    style={{ width: '100%', height: '100%' }}
                >
                    Tu navegador no soporta la visualización de PDFs.
                </iframe>
            );
        }

        return (
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'column', 
                alignItems: 'center',
                height: '100%',
                width: '100%',
                backgroundColor: 'lightgray'
            }}>
                {getFileIcon(file.fileName)}
                <Typography
                    variant="caption"
                    sx={{ fontFamily: 'Poppins', color: 'gray', mt: 2, textAlign: 'center' }} 
                >
                    {file.fileName} <br />
                    Este documento no se puede previsualizar en el navegador.
                </Typography>
            </Box>
        );
    };

    return (
        <Dialog onClose={onClose} open={open} maxWidth="lg" fullWidth>
            <Box>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>
            <Typography variant="h6" sx={{ mt: 2, mb: 1, ml: 3 }}>
                {__("labels.16")}
            </Typography>
            <Divider />

            {open && (
                <Stack direction={{ xs: "column-reverse", md: "row" }} className='p-4'>
                    <Box className="max-w-auto md:max-w-[300px] max-h-[300px] md:max-h-[400px] overflow-scroll overflow-x-hidden">
                        {map(get(current, "data.files"), (pict, i) => (
                            <ButtonBase className="flex flex-row" onClick={onClick(pict)} key={i}>
                                <Box>
                                    {/* Si es una imagen, muestra la miniatura, si no, el ícono correspondiente */}
                                    {getFileIcon(pict.fileName) ? (
                                        <Avatar
                                            className='border border-black rounded'
                                            variant="square"
                                            sx={{ backgroundColor: 'white', border: 'none' }} // Fondo blanco y sin borde
                                        >
                                            {getFileIcon(pict.fileName)}
                                        </Avatar>
                                    ) : (
                                        <Avatar
                                            className='border border-black rounded'
                                            variant="square"
                                            alt={`pict-${i + 1}`}
                                            src={pict.url + pict.fileName}
                                            sx={{ backgroundColor: 'white' }} // Fondo blanco para las imágenes también
                                        />
                                    )}
                                </Box>
                                <Box className="p-2 text-left">
                                    <Typography className='!leading-[1.2]' component="div" variant="h6">{i + 1}</Typography>
                                    <Typography className='!leading-[1.2] w-[150px] !truncate' component="div" variant="caption">{pict.localPath}</Typography>
                                </Box>
                                <IconButton
                                    sx={{ marginLeft: 'auto', 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center',
                                        height: '100%'
                                    }}
                                    href={pict.url + pict.fileName} 
                                    download={pict.fileName} // Forzar la descarga
                                >
                                    <DownloadIcon sx={{ color: 'blue' }} />
                                </IconButton>
                                <IconButton
                                    onClick={() => {
                                        if (canView("TRANSACCIONES/RUTAS/PUNTOS DE ENTREGA/ADJUNTOS", "delete")) {
                                            onDelete(pict.fileName)
                                        } else {
                                            setAlert({
                                                open: true,
                                                title: "Permiso Denegado.",
                                                subtitle: (<ul> • No tienes permiso para eliminar el archivo del punto de entrega.</ul>),
                                                type: "error",
                                                btn: __(`${module}.actions.close`),
                                                func: closeAlert
                                            })
                                        }
                                    }}
                                    sx={{ marginLeft: 'auto', 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center',
                                        height: '100%',
                                        color: 'red'
                                    }}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </ButtonBase>
                        ))}
                    </Box>

                    <Box className='flex-1 max-h-[300px] md:max-h-[400px] h-[400px] mb-4 sm:mb-0 w-full rounded overflow-hidden pl-4'>
                        {!isEmpty(get(current, "data.files"))
                            ? (
                                <div className='flex justify-center items-center w-full h-[300px] md:h-full max-h-full max-w-full bg-gray-200'>
                                    {/* Previsualización de imágenes o PDF */}
                                    {renderPreview(imgSelect)}
                                </div>
                            ) : (
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: "column",
                                    height: 400,
                                    width: '100%',
                                    justifyContent: "center",
                                    alignItems: "center"
                                }}>
                                    <Stack justifyContent="center" alignItems="center" spacing={1} pt={2} >
                                        <Typography variant="bml">{__(`table.empty.archives.name`)}</Typography>
                                        <Typography variant="caption">{__(`table.empty.archives.info`)}</Typography>
                                    </Stack>
                                </Box>
                            )}
                    </Box>
                </Stack>
            )}

                {(get(post, "isLoading", false) || get(delet, "isLoading", false)) && (
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        backgroundColor: 'white', // Fondo blanco
                        borderRadius: 2,
                        padding: 2,
                        boxShadow: 3
                    }}>
                        <CircularProgress />
                        <Typography variant="caption" sx={{ mt: 2 }}>
                            {__("Cargando...")}
                        </Typography>
                    </Box>
                )}

            {/* Botón para subir archivos */}
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <Button
                    variant="contained"
                    component="label"
                    color="primary"
                    onClick={() => {
                        if (canView("TRANSACCIONES/RUTAS/PUNTOS DE ENTREGA/ADJUNTOS", "add")) {
                            setOpenModal(true)
                        } else {
                            setAlert({
                                open: true,
                                title: "Permiso Denegado.",
                                subtitle: (<ul> • No tienes permiso para agregar archivos al punto de entrega.</ul>),
                                type: "error",
                                btn: __(`${module}.actions.close`),
                                func: closeAlert
                            })
                        }
                    }}>
                    Adjuntar archivos
                </Button>
            </Box>
        <AttachFilesModalDP 
            id={id}
            open={openModal}
            close={() => setOpenModal(false)}
            file = {HandleUploadFile}
            />
        </Dialog>
    );
}

export default FilesModal;
